import {
  append,
  update,
  clone,
  omit,
  assocPath,
  remove,
  mergeDeepRight,
} from 'ramda';
import { IFormData, IUpdateValue } from './BusinessDataFormUtils';

const getAssociationName = (mutationKey: string): string => {
  return mutationKey.split('_')[0] ?? '';
};

const getMutationOperation = (mutationKey: string): string => {
  return mutationKey.substring(mutationKey.indexOf('_') + 1);
};

const findAssociationIndex = (
  leftArray: IUpdateValue[],
  association: IUpdateValue
): number => {
  return leftArray?.findIndex((leftAssociation: IUpdateValue) => {
    const leftId: string | undefined =
      leftAssociation.id != null
        ? leftAssociation.id
        : typeof leftAssociation === 'string'
        ? leftAssociation
        : undefined;

    const rightId: string | undefined =
      association.id != null
        ? association.id
        : typeof association === 'string'
        ? association
        : undefined;

    return leftId && rightId && leftId === rightId;
  });
};

const removeAssociationFromOldValues = (
  oldValues: IUpdateValue,
  key: string,
  newValue: IUpdateValue
): {
  updatedOldValues: IUpdateValue | undefined;
  foundInOldValues: boolean;
} => {
  const leftAssocationIndex = findAssociationIndex(oldValues[key], newValue);
  if (leftAssocationIndex > -1) {
    return {
      updatedOldValues: remove(leftAssocationIndex, 1, oldValues[key]),
      foundInOldValues: true,
    };
  } else {
    return { updatedOldValues: undefined, foundInOldValues: false };
  }
};

const mergeManyOperation = (
  leftAssociationsClone: IUpdateValue,
  rightAssociation: IUpdateValue,
  key: string
) => {
  const leftAssocationIndex = findAssociationIndex(
    leftAssociationsClone[key],
    rightAssociation
  );
  if (leftAssocationIndex > -1) {
    return update(
      leftAssocationIndex,
      mergeBusinessDataMutations(
        leftAssociationsClone[key][leftAssocationIndex],
        rightAssociation
      ),
      leftAssociationsClone[key]
    );
  } else {
    return append(rightAssociation, leftAssociationsClone[key]);
  }
};

const mergeAssociations = (
  leftAssociations: IUpdateValue,
  rightAssociations: IUpdateValue
): IUpdateValue => {
  let leftAssociationsClone = clone(leftAssociations);

  Object.entries(rightAssociations).forEach(([key, value]) => {
    const associationName = getAssociationName(key);
    const mutationOperation = getMutationOperation(key);

    if (mutationOperation === 'Values') {
      leftAssociationsClone[key] = mergeBusinessDataMutations(
        leftAssociationsClone[key],
        value
      );
    } else if (mutationOperation === 'Ref') {
      leftAssociationsClone[key] =
        typeof value === 'string' || value === null
          ? value
          : mergeBusinessDataMutations(leftAssociationsClone[key], value);
    } else if (mutationOperation === 'Update_Values') {
      rightAssociations[key]?.forEach((rightAssociation: IUpdateValue) => {
        const leftAssocationIndex = leftAssociationsClone[key]?.findIndex(
          (leftAssociation: IUpdateValue) =>
            leftAssociation.id === rightAssociation.id
        );
        if (leftAssocationIndex > -1) {
          leftAssociationsClone[key] = update(
            leftAssocationIndex,
            mergeBusinessDataMutations(
              leftAssociationsClone[key][leftAssocationIndex],
              rightAssociation
            ),
            leftAssociationsClone[key]
          );
        } else {
          leftAssociationsClone[key] = append(
            rightAssociation,
            leftAssociationsClone[key]
          );
        }
      });
    } else if (mutationOperation === 'Delete_Values') {
      rightAssociations[key]?.forEach((rightAssociation: IUpdateValue) => {
        let foundInAddedValues = false;
        ['Add_Values', 'Update_Values', 'Undelete'].forEach(
          operationToRemoveFrom => {
            const key = `${associationName}_${operationToRemoveFrom}`;
            const oldAssociatinsRemoved = removeAssociationFromOldValues(
              leftAssociationsClone,
              key,
              rightAssociation
            );
            if (oldAssociatinsRemoved.foundInOldValues) {
              leftAssociationsClone[key] =
                oldAssociatinsRemoved.updatedOldValues;
            }
            if (
              oldAssociatinsRemoved.foundInOldValues &&
              ['Add_Values', 'Undelete'].includes(operationToRemoveFrom)
            ) {
              foundInAddedValues = true;
            }
          }
        );

        if (!foundInAddedValues) {
          leftAssociationsClone[key] = mergeManyOperation(
            leftAssociationsClone,
            rightAssociation,
            key
          );
        }
      });
    } else if (mutationOperation === 'Add_Values') {
      rightAssociations[key]?.forEach((rightAssociation: IUpdateValue) => {
        const foundInOldValues = ['Delete_Values'].some(
          operationToRemoveFrom => {
            const key = `${associationName}_${operationToRemoveFrom}`;
            const oldAssociatinsRemoved = removeAssociationFromOldValues(
              leftAssociationsClone,
              key,
              rightAssociation
            );
            if (oldAssociatinsRemoved.foundInOldValues) {
              leftAssociationsClone[key] =
                oldAssociatinsRemoved.updatedOldValues;
            }
            return oldAssociatinsRemoved.foundInOldValues;
          }
        );

        if (!foundInOldValues) {
          leftAssociationsClone[key] = mergeManyOperation(
            leftAssociationsClone,
            rightAssociation,
            key
          );
        }
      });
    } else if (mutationOperation === 'Undelete') {
      rightAssociations[key]?.forEach((rightAssociation: IUpdateValue) => {
        const foundInOldValues = ['Delete_Values'].some(
          operationToRemoveFrom => {
            const key = `${associationName}_${operationToRemoveFrom}`;
            const oldAssociatinsRemoved = removeAssociationFromOldValues(
              leftAssociationsClone,
              key,
              rightAssociation
            );
            if (oldAssociatinsRemoved.foundInOldValues) {
              leftAssociationsClone[key] =
                oldAssociatinsRemoved.updatedOldValues;
            }
            return oldAssociatinsRemoved.foundInOldValues;
          }
        );

        if (!foundInOldValues) {
          leftAssociationsClone[key] = mergeManyOperation(
            leftAssociationsClone,
            rightAssociation,
            key
          );
        }
      });
    } else if (mutationOperation === 'Delete_Refs') {
      rightAssociations[key]?.forEach((rightAssociation: IUpdateValue) => {
        const foundInOldValues = ['Add_Refs'].some(operationToRemoveFrom => {
          const key = `${associationName}_${operationToRemoveFrom}`;
          const oldAssociatinsRemoved = removeAssociationFromOldValues(
            leftAssociationsClone,
            key,
            rightAssociation
          );
          if (oldAssociatinsRemoved.foundInOldValues) {
            leftAssociationsClone[key] = oldAssociatinsRemoved.updatedOldValues;
          }
          return oldAssociatinsRemoved.foundInOldValues;
        });

        if (!foundInOldValues) {
          leftAssociationsClone[key] = mergeManyOperation(
            leftAssociationsClone,
            rightAssociation,
            key
          );
        }
      });
    } else if (mutationOperation === 'Add_Refs') {
      rightAssociations[key]?.forEach((rightAssociation: IUpdateValue) => {
        const foundInOldValues = ['Delete_Refs'].some(operationToRemoveFrom => {
          const key = `${associationName}_${operationToRemoveFrom}`;
          const oldAssociatinsRemoved = removeAssociationFromOldValues(
            leftAssociationsClone,
            key,
            rightAssociation
          );
          if (oldAssociatinsRemoved.foundInOldValues) {
            leftAssociationsClone[key] = oldAssociatinsRemoved.updatedOldValues;
          }
          return oldAssociatinsRemoved.foundInOldValues;
        });

        if (!foundInOldValues) {
          leftAssociationsClone[key] = mergeManyOperation(
            leftAssociationsClone,
            rightAssociation,
            key
          );
        }
      });
    }
  });

  return leftAssociationsClone;
};

/**
 * Merges two businessDataMutations
 *
 * Values with same key will be overwritten by the second object
 * Will resolve if same object exists in multiple operations (ex. Add and Delete)
 *
 */
export const mergeBusinessDataMutations = (
  leftValue: IFormData,
  rightValue: IFormData
): IFormData => {
  const ownPropsMerged: IFormData = mergeDeepRight(
    omit(['_Associations'], leftValue),
    omit(['_Associations'], rightValue)
  );

  let associationsMerged;
  if (leftValue?._Associations || rightValue?._Associations) {
    associationsMerged = mergeAssociations(
      leftValue?._Associations ?? {},
      rightValue?._Associations ?? {}
    );
  }

  const newValue: IFormData = associationsMerged
    ? assocPath(['_Associations'], associationsMerged, ownPropsMerged)
    : ownPropsMerged;

  return newValue;
};
